import React, { useContext, useEffect, useRef, useState } from "react";
import {
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/header.scss";

const categories = [
  { label: "Monthly offer", to: "/shop?collection=monthly", accent: "coral" },
  { label: "Hot deals", to: "/shop?collection=deals", accent: "red" },
  { label: "Cheese", to: "/shop?category=Cheese" },
  { label: "Meat & Seafood", to: "/shop?category=Meat%20%26%20Seafood" },
  { label: "Fruit & Veg", to: "/shop?category=Fruit%20%26%20Veg" },
  { label: "Grocery", to: "/shop?category=Grocery" },
  { label: "Drinks", to: "/shop?category=Drinks" },
];

function Header() {
  const { itemCount, wishlist } = useContext(CartContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const searchInput = useRef(null);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.classList.toggle("nav-open", mobileOpen || searchOpen);
    return () => document.body.classList.remove("nav-open");
  }, [mobileOpen, searchOpen]);

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 48);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  const suggestions = query.trim()
    ? productData
        .filter((product) =>
          `${product.name} ${product.category} ${product.subcategory}`
            .toLowerCase()
            .includes(query.toLowerCase())
        )
        .slice(0, 5)
    : [];

  const submitSearch = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    navigate(`/shop?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
  };

  return (
    <>
      <header className={`site-header ${pathname === "/" ? "site-header--home" : ""} ${pathname === "/" && !scrolled ? "site-header--overlay" : ""}`}>
        <div className="announcement-bar">
          <p>{siteConfig.announcement}</p>
          <div>
            <Link to="/shipping">Delivery guide</Link>
            <span aria-hidden="true">·</span>
            <Link to="/contact">Need help?</Link>
          </div>
        </div>

        <div className="header-main container">
          <button
            className="icon-button menu-toggle"
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <FiMenu />
          </button>

          <Link className="brand" to="/" aria-label={`${siteConfig.brandName} home`}>
            <img src={siteConfig.logo} alt={siteConfig.brandName} />
          </Link>

          <nav className="header-links" aria-label="Primary navigation">
            <NavLink to="/shop">The Shop</NavLink>
            <NavLink to="/about">Our Story</NavLink>
            <NavLink to="/shipping">How it works</NavLink>
            <NavLink to="/contact">Contact</NavLink>
            <div className="more-menu">
              <button type="button" onClick={() => setMoreOpen((open) => !open)} aria-expanded={moreOpen}>More</button>
              {moreOpen && <div className="more-menu__panel">
                <Link to="/privacy">Privacy policy</Link>
                <Link to="/cookies">Cookie policy</Link>
                <Link to="/terms">Terms</Link>
                <Link to="/faqs">FAQs</Link>
              </div>}
            </div>
          </nav>

          <div className="header-actions">
            <button
              className="icon-button"
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <FiSearch />
            </button>
            <Link className="icon-button desktop-action" to="/account" aria-label="Account">
              <FiUser />
            </Link>
            <Link
              className="icon-button desktop-action"
              to="/shop?favourites=true"
              aria-label={`${wishlist.length} favourites`}
            >
              <FiHeart />
              {wishlist.length > 0 && <span className="action-count">{wishlist.length}</span>}
            </Link>
            <Link className="cart-link" to="/cartpage" aria-label={`${itemCount} cart items`}>
              <FiShoppingBag />
              <span>Cart</span>
              <b>{itemCount}</b>
            </Link>
          </div>
        </div>

        <nav className="category-nav" aria-label="Product categories">
          <div className="container category-nav__inner">
            {categories.map((item) => (
              <Link
                key={item.label}
                className={item.accent ? `category-link category-link--${item.accent}` : "category-link"}
                to={item.to}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
      </header>

      <div className={`drawer-overlay ${mobileOpen ? "is-open" : ""}`}>
        <button
          className="overlay-dismiss"
          type="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
        <aside className="mobile-drawer" aria-label="Mobile navigation">
          <div className="mobile-drawer__head">
            <span>Menu</span>
            <button type="button" className="icon-button" onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <FiX />
            </button>
          </div>
          <nav>
            <Link to="/shop">Shop all</Link>
            {categories.map((item) => (
              <Link key={item.label} to={item.to}>{item.label}</Link>
            ))}
          </nav>
          <div className="mobile-drawer__secondary">
            <Link to="/account">Your account</Link>
            <Link to="/about">Our story</Link>
            <Link to="/shipping">Delivery guide</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/faqs">FAQs</Link>
            <Link to="/cookies">Cookie policy</Link>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </aside>
      </div>

      <div className={`search-panel ${searchOpen ? "is-open" : ""}`}>
        <button className="search-panel__backdrop" type="button" aria-label="Close search" onClick={() => setSearchOpen(false)} />
        <div className="search-panel__dialog" role="dialog" aria-modal="true" aria-label="Search products">
          <div className="container">
            <div className="search-panel__top">
              <p className="eyebrow">Find something delicious</p>
              <button type="button" className="icon-button" onClick={() => setSearchOpen(false)} aria-label="Close search">
                <FiX />
              </button>
            </div>
            <form onSubmit={submitSearch} className="search-form">
              <FiSearch />
              <input
                ref={searchInput}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search cheese, origin or pairing..."
              />
              <button className="button button--primary" type="submit">Search</button>
            </form>
            {query && (
              <div className="search-suggestions">
                {suggestions.length ? suggestions.map((product) => (
                  <Link key={product.id} to={`/shopDetails/${product.id}`}>
                    <img src={product.image} alt="" />
                    <span><b>{product.name}</b><small>{product.category} · {product.unit}</small></span>
                  </Link>
                )) : <p>No products match “{query}”.</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
